// helpers/getSchemasEligibleForPerspectiveAssignment.ts

import { Schema } from 'types/schema'; // Adjust the import path as needed

// Define an interface for schema details
export interface SchemaItem {
  schema_id: string;
  version_id: string;
  name: string;
  node_label: string;
  system_name: string;
  description: string | null;
  glyph: string | null;
  glyph_color: import('types/glyph').GlyphColor | null;
}

// Define the result interface
export interface EligibleSchemasResult {
  eligibleSchemas: SchemaItem[];
  categories: SchemaItem[];
}

/**
 * Helper function to find the first abstract ancestor of a schema.
 * Excludes 'BaseSchema'.
 *
 * @param schema - The schema to find the ancestor for.
 * @param schemaMap - A map of schema_id to Schema for quick lookup.
 * @returns The SchemaItem object of the first abstract ancestor or null if not found.
 */
function findFirstAbstractAncestor(
  schema: Schema,
  schemaMap: Map<string, Schema>
): SchemaItem | null {
  let currentSchema = schema;
  const visited = new Set<string>();

  while (currentSchema.parent_schema_id) {
    // Prevent infinite loops in case of circular references
    if (visited.has(currentSchema.parent_schema_id)) {
      console.warn(
        `Circular reference detected for schema_id: ${currentSchema.parent_schema_id}`
      );
      break;
    }
    visited.add(currentSchema.parent_schema_id);

    const parentSchema = schemaMap.get(currentSchema.parent_schema_id);
    if (!parentSchema) {
      console.warn(
        `Parent schema not found for schema_id: ${currentSchema.parent_schema_id}`
      );
      break;
    }

    // Exclude BaseSchema
    if (parentSchema.system_name === 'BaseSchema') {
      break;
    }

    if (parentSchema.is_abstract) {
      return {
        schema_id: parentSchema.schema_id,
        version_id: parentSchema.version_id,
        name: parentSchema.name,
        node_label: parentSchema.node_label || '',
        system_name: parentSchema.system_name,
        description: parentSchema.description,
        glyph: parentSchema.glyph,
        glyph_color: parentSchema.glyph_color,
      };
    }

    currentSchema = parentSchema;
  }

  return null;
}

/**
 * Determines which schemas are eligible to have a specific assignee assigned to a specific perspective.
 *
 * @param allSchemas - The entire array of Schema objects.
 * @param assigneeSystemName - The system_name of the assignee sphere (e.g., "center_priority").
 * @param perspectiveSystemName - The system_name of the perspective (e.g., "center_parent").
 * @returns An object containing eligible schemas and their categories.
 */
export function getSchemasEligibleForPerspectiveAssignment(
  allSchemas: Schema[],
  assigneeSystemName: string,
  perspectiveSystemName: string
): EligibleSchemasResult {
  console.log(
    '------------------- getSchemasEligibleForPerspectiveAssignment -------------------'
  );
  console.log('Assignee System Name:', assigneeSystemName);
  console.log('Perspective System Name:', perspectiveSystemName);
  console.log(
    '-------------------------------------------------------------------------------'
  );

  // Create a map for quick schema lookups by schema_id
  const schemaMap = new Map<string, Schema>();
  allSchemas.forEach((schema) => schemaMap.set(schema.schema_id, schema));

  // Initialize an array to hold eligible schemas
  const eligibleSchemas: SchemaItem[] = [];

  // Iterate through all schemas to find those with the specified perspective
  allSchemas.forEach((schema) => {
    // Skip abstract schemas themselves
    if (schema.is_abstract) {
      return;
    }

    const perspective = schema.perspectives?.find(
      (p) => p.system_name === perspectiveSystemName
    );

    if (perspective) {
      const filters = perspective.assignee_filter || [];

      let isEligible = false;

      if (filters.length === 0) {
        // If no filters, allow all non-abstract schemas
        const assigneeSchema = allSchemas.find(
          (s) => s.system_name === assigneeSystemName
        );
        if (assigneeSchema && !assigneeSchema.is_abstract) {
          isEligible = true;
        }
      } else {
        // Check if the assignee meets any of the filter criteria
        isEligible = filters.some(({ property, value }) => {
          if (property === 'type') {
            return value === assigneeSystemName;
          } else if (property === 'label') {
            // Assuming assignee has a label, adjust accordingly
            const assigneeSchema = allSchemas.find(
              (s) => s.system_name === assigneeSystemName
            );
            return assigneeSchema ? assigneeSchema.node_label === value : false;
          }
          // Extend with other properties if needed
          return false;
        });
      }

      if (isEligible && schema.system_name !== 'center_baseschema') {
        eligibleSchemas.push({
          schema_id: schema.schema_id,
          version_id: schema.version_id,
          name: schema.name,
          node_label: schema.node_label || '',
          system_name: schema.system_name,
          description: schema.description,
          glyph: schema.glyph,
          glyph_color: schema.glyph_color,
        });
      }
    }
  });

  console.log(`Eligible Schemas Count: ${eligibleSchemas.length}`);

  // Determine categories based on the first abstract ancestors
  const categoriesSet = new Set<string>();
  const categories: SchemaItem[] = [];

  eligibleSchemas.forEach((schemaItem) => {
    const schema = allSchemas.find(
      (s) => s.system_name === schemaItem.system_name
    );
    if (schema) {
      const category = findFirstAbstractAncestor(schema, schemaMap);
      if (
        category &&
        !categoriesSet.has(category.schema_id) &&
        category.system_name !== 'center_baseschema'
      ) {
        categoriesSet.add(category.schema_id);
        categories.push(category);
      }
    }
  });

  console.log(`Categories Count: ${categories.length}`);

  return { eligibleSchemas, categories };
}
